import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';

import { alpha } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AssignEvaluatorModal from '../components/Procurement/AssignEvaluatorModal';
import ClarificationsView from '../components/Procurement/ClarificationsView';
import FinalReportExport from '../components/Procurement/FinalReportExport';
import ModerationConsole from '../components/Procurement/ModerationConsole';
import PreEvaluationView from '../components/Procurement/PreEvaluationView';
import ProcurementOverview from '../components/Procurement/ProcurementOverview';
import SupplierAssessmentMatrix from '../components/Procurement/SupplierAssessmentMatrix';
import SupplierConsolidatedDashboard from '../components/Procurement/SupplierConsolidatedDashboard';
import { useAccount } from '../contexts/AccountContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useProcurementDetails } from '../hooks/useProcurementDetails';
import {
  saveEvaluation,
  updateSupplierStatus,
  uploadBulkSupplierSubmissions,
} from '../services/procurementService';
import type { EvaluationScore, Procurement, ProcurementSettings } from '../types/procurement';
import type { StagedSupplier } from '../utils/fileUploadUtils';
import { markProcurementVisited } from '../utils/recentProcurements';

const EMPTY_ARRAY: never[] = [];

const ProcurementDetailPage: React.FC = () => {
  const { procurementId } = useParams<{ procurementId: string }>();
  const [tab, setTab] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [evaluatingSupplierId, setEvaluatingSupplierId] = useState<string | null>(null);
  const [assignEvaluatorSupplierId, setAssignEvaluatorSupplierId] = useState<string | null>(null);

  const {
    procurement,
    suppliers,
    rubric,
    evaluations,
    assessments,
    quickAnalyses,
    evaluatorAssignments,
    loading,
    error,
  } = useProcurementDetails(procurementId);
  const { showSnackbar } = useSnackbar();
  const { account } = useAccount();

  const isPanelChair =
    account?.uid === procurement?.ownerId ||
    account?.role === 'Admin' ||
    account?.role === 'Super Admin';

  useEffect(() => {
    if (procurement?.id && account?.uid) {
      markProcurementVisited(account.uid, procurement.id);
    }
  }, [procurement?.id, account?.uid]);

  // Auto-calculate and advance procurement status
  useEffect(() => {
    if (!procurement || !isPanelChair) {
      return;
    }

    // We only auto-calculate if it's no longer a draft and not archived
    if (['PUBLISHED', 'EVALUATION', 'AWARDED'].includes(procurement.status)) {
      const anyAwarded = suppliers.some((s) => s.status === 'AWARDED');
      const hasEvaluationsStarted =
        evaluations.length > 0 || suppliers.some((s) => s.status === 'EVALUATING');

      let computedStatus: Procurement['status'] = 'PUBLISHED';
      if (anyAwarded) {
        computedStatus = 'AWARDED';
      } else if (hasEvaluationsStarted) {
        computedStatus = 'EVALUATION';
      }

      // If the computed status differs from the DB, update it seamlessly in the background
      if (computedStatus !== procurement.status) {
        import('../services/procurementService').then(({ updateProcurement }) => {
          updateProcurement(procurement.id, { status: computedStatus }).catch(() => {
            // Ignore background sync errors
          });
        });
      }
    }
  }, [procurement, suppliers, evaluations, isPanelChair]);

  const authorizedSuppliers = isPanelChair
    ? suppliers
    : suppliers.filter((s) =>
        evaluatorAssignments.some((ea) => ea.userId === account?.uid && ea.supplierId === s.id)
      );

  const handleSaveEvaluation = async (
    supplierId: string,
    scores: EvaluationScore[],
    comments: string,
    isAutoSave?: boolean,
    status?: 'DRAFT' | 'SUBMITTED'
  ) => {
    if (!procurementId || !account?.uid) {
      return false;
    }

    const existingEval = evaluations.find(
      (e) => e.supplierId === supplierId && e.evaluatorId === account.uid
    );
    const evaluationId = existingEval?.id || '';

    const result = await saveEvaluation(
      procurementId,
      supplierId,
      evaluationId,
      account.uid,
      scores,
      comments,
      status || (existingEval?.status ?? 'DRAFT')
    );

    if (!isAutoSave) {
      if (result.success) {
        showSnackbar('Evaluation saved successfully', 'success');
      } else {
        showSnackbar(`Failed to save evaluation: ${result.error}`, 'error');
      }
    }

    return result.success;
  };

  const handleUploadAll = async (stagedSuppliers: StagedSupplier[]) => {
    if (!procurement || !account?.uid || !account?.teamId || !procurementId) {
      return;
    }

    setIsUploading(true);
    try {
      await uploadBulkSupplierSubmissions(
        procurement.clientId,
        account.teamId,
        account.uid,
        procurementId,
        stagedSuppliers
      );
      showSnackbar('Submissions uploaded successfully!', 'success');
    } catch (e: unknown) {
      showSnackbar(`Upload failed: ${(e as Error).message}`, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !procurement) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error?.message || 'Procurement not found'}</Alert>
      </Box>
    );
  }

  const renderTabLabel = (number: number, label: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
          color: (theme) => (theme.palette.mode === 'dark' ? '#000' : '#fff'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 'bold',
        }}
      >
        {number}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ px: 3, pb: 3, pt: 0 }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          bgcolor: 'background.default',
          pt: 3,
          pb: 1,
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ mb: 2 }}>
          {procurement.title || procurementId}
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tab}
            onChange={(_, newValue) => setTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              value={0}
              label={renderTabLabel(1, 'Overview')}
              disabled={procurement.status === 'DRAFT'}
            />
            <Tab
              value={1}
              label={renderTabLabel(2, 'Clarifications')}
              disabled={procurement.status === 'DRAFT'}
            />
            {isPanelChair && (
              <Tab
                value={2}
                label={renderTabLabel(3, 'Suppliers')}
                disabled={procurement.status === 'DRAFT'}
              />
            )}
            {isPanelChair && (
              <Tab
                value={3}
                label={renderTabLabel(4, 'Compliance')}
                disabled={procurement.status === 'DRAFT'}
              />
            )}
            <Tab
              value={4}
              label={renderTabLabel(isPanelChair ? 5 : 3, 'Assessment')}
              disabled={procurement.status === 'DRAFT'}
            />
            {isPanelChair && (
              <Tab
                value={5}
                label={renderTabLabel(6, 'Moderation')}
                disabled={procurement.status === 'DRAFT'}
              />
            )}
            {isPanelChair && (
              <Tab
                value={6}
                label={renderTabLabel(7, 'Export')}
                disabled={procurement.status === 'DRAFT'}
              />
            )}
          </Tabs>
        </Box>
      </Box>

      {tab === 0 && (
        <ProcurementOverview
          procurement={procurement}
          suppliers={suppliers}
          evaluations={evaluations}
          evaluatorAssignments={evaluatorAssignments}
          rubric={rubric}
          isPanelChair={isPanelChair}
          onSave={async (updatedData) => {
            const { updateProcurement } = await import('../services/procurementService');
            try {
              await updateProcurement(procurement.id, updatedData);
              showSnackbar('Procurement details updated successfully', 'success');
            } catch (err) {
              showSnackbar((err as Error).message, 'error');
            }
          }}
          onSaveRubric={async (criteria) => {
            const { createRubric } = await import('../services/procurementService');
            try {
              await createRubric(procurement.id, criteria, account?.uid || 'Unknown');
              showSnackbar('Rubric updated successfully', 'success');
            } catch (err) {
              showSnackbar((err as Error).message, 'error');
            }
          }}
        />
      )}

      {tab === 1 && (
        <ClarificationsView
          procurementId={procurement.id}
          initialClarifications={procurement.clarifications || []}
          onUpdate={async (clarifications) => {
            try {
              const { doc, setDoc } = await import('firebase/firestore');
              const { firestore: db } = await import('../services/firebase');
              await setDoc(
                doc(db, 'procurements', procurement.id),
                { clarifications },
                { merge: true }
              );
            } catch (_err) {
              // Ignore silent failure
            }
          }}
          onShowSnackbar={(msg, sev) => showSnackbar(msg, sev)}
        />
      )}

      {tab === 2 && isPanelChair && (
        <SupplierConsolidatedDashboard
          suppliers={suppliers}
          rubric={rubric}
          evaluations={evaluations}
          assessments={assessments}
          evaluatorAssignments={evaluatorAssignments}
          isPanelChair={isPanelChair}
          isUploading={isUploading}
          onUploadAll={handleUploadAll}
          onUpdateStatus={async (supplierId, newStatus) => {
            const res = await updateSupplierStatus(procurement.id, supplierId, newStatus);
            if (res.success) {
              showSnackbar(`Supplier status updated to ${newStatus}`, 'success');
            } else {
              showSnackbar(`Failed to update status: ${res.error}`, 'error');
            }
          }}
          onAddEvaluatorClick={(supplierId) => {
            setAssignEvaluatorSupplierId(supplierId);
          }}
          onOpenEvaluation={(supplierId) => {
            setEvaluatingSupplierId(supplierId);
            setTab(4); // Navigate to Assessment tab
          }}
          onShowSnackbar={(msg, sev) => showSnackbar(msg, sev)}
        />
      )}

      {tab === 3 && isPanelChair && (
        <PreEvaluationView
          procurementId={procurement.id}
          procurement={procurement}
          suppliers={suppliers}
          assessments={assessments}
          onSaveSettings={async (updatedSettings) => {
            try {
              const { doc, updateDoc } = await import('firebase/firestore');
              const { firestore: db } = await import('../services/firebase');
              await updateDoc(doc(db, 'procurements', procurement.id), {
                settings: updatedSettings,
              });
              showSnackbar('AI Settings updated successfully.', 'success');
            } catch (err) {
              showSnackbar(`Failed to update settings: ${(err as Error).message}`, 'error');
            }
          }}
          onShowSnackbar={(msg, sev) => showSnackbar(msg, sev)}
        />
      )}

      {tab === 4 && rubric && authorizedSuppliers.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Supplier Assessment
            </Typography>
            <Box component="ul" sx={{ mt: 0, pl: 2, color: 'text.secondary', '& li': { mb: 0.5 } }}>
              <Typography component="li" variant="body2">
                Review the supplier's submitted documents.
              </Typography>
              <Typography component="li" variant="body2">
                Score submissions against the evaluation rubric.
              </Typography>
              {procurement.settings?.evaluation?.enabled !== false ? (
                <Typography component="li" variant="body2">
                  Use the AI Evaluation Assistant to auto-draft initial scores and justifications.
                </Typography>
              ) : (
                <Typography component="li" variant="body2" color="warning.dark">
                  The AI Evaluation Assistant is currently disabled by the Panel Chair. All scoring
                  must be completed manually.
                </Typography>
              )}
            </Box>
          </Box>

          {isPanelChair && (
            <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
              <CardHeader
                sx={{ py: 1.5 }}
                title="AI Evaluation Settings"
                titleTypographyProps={{ variant: 'subtitle2', fontWeight: 'bold' }}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={4} alignItems="flex-start">
                  <Grid
                    size={{ xs: 12, md: 5 }}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={procurement.settings?.evaluation?.enabled ?? true}
                          onChange={async (e) => {
                            const updatedSettings = {
                              ...(procurement.settings || {}),
                              evaluation: {
                                ...(procurement.settings?.evaluation || {}),
                                enabled: e.target.checked,
                              },
                            } as ProcurementSettings;
                            try {
                              const { doc, updateDoc } = await import('firebase/firestore');
                              const { firestore: db } = await import('../services/firebase');
                              await updateDoc(doc(db, 'procurements', procurement.id), {
                                settings: updatedSettings,
                              });
                              showSnackbar('AI Settings updated successfully.', 'success');
                            } catch (err) {
                              showSnackbar(
                                `Failed to update settings: ${(err as Error).message}`,
                                'error'
                              );
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            Enable AI Evaluator
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Allow evaluators to auto-generate draft scores.
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={procurement.settings?.evaluation?.useFuzzyScoring ?? true}
                          onChange={async (e) => {
                            const updatedSettings = {
                              ...(procurement.settings || {}),
                              evaluation: {
                                ...(procurement.settings?.evaluation || {}),
                                useFuzzyScoring: e.target.checked,
                              },
                            } as ProcurementSettings;
                            try {
                              const { doc, updateDoc } = await import('firebase/firestore');
                              const { firestore: db } = await import('../services/firebase');
                              await updateDoc(doc(db, 'procurements', procurement.id), {
                                settings: updatedSettings,
                              });
                              showSnackbar('AI Settings updated successfully.', 'success');
                            } catch (err) {
                              showSnackbar(
                                `Failed to update settings: ${(err as Error).message}`,
                                'error'
                              );
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            Nuanced Scoring Analysis
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            AI evaluates context beyond exact keyword matches.
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      <strong>Provide Scoring Suggestions:</strong> The AI analyzes the documents
                      and offers recommended scores and reasoning for human review without
                      overwriting your existing inputs.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      <strong>Pre-fill Scores & Justifications:</strong> The AI directly populates
                      the evaluation scorecard with its best assessment based on the rubric, ready
                      for human verification.
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        bgcolor: 'action.hover',
                        p: 1.5,
                        borderRadius: 1,
                        mt: 2,
                        mx: 'auto',
                        maxWidth: { xs: '100%', md: '75%' },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ display: 'block', textAlign: 'left' }}
                      >
                        <strong>Note on AI Bias:</strong> AI summaries may omit nuanced context or
                        inappropriately highlight specific details. To ensure fairness, these tools
                        assist but never replace human judgment. Evaluators must verify all
                        AI-generated findings against the original source documents before
                        finalizing their decisions.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {authorizedSuppliers.map((supplier) => {
              const isExpanded = supplier.id === evaluatingSupplierId;
              const userEval = evaluations.find(
                (e) => e.supplierId === supplier.id && e.evaluatorId === account?.uid
              );

              let statusLabel = 'No Responses';
              let statusColor: 'error' | 'success' | 'warning' = 'error';

              if (userEval?.status === 'SUBMITTED') {
                statusLabel = 'Submitted';
                statusColor = 'success';
              } else if (
                userEval?.status === 'DRAFT' ||
                (userEval?.scores && userEval.scores.length > 0)
              ) {
                statusLabel = 'Some Responses';
                statusColor = 'warning';
              }

              const isYellow = statusLabel === 'Some Responses';
              const hasIngestionWarning = supplier.documents?.some(
                (d) =>
                  d.conversionStatus === 'failed' || d.status === 'error' || d.status === 'corrupt'
              );

              return (
                <Accordion
                  key={supplier.id}
                  expanded={isExpanded}
                  onChange={(_, expanded) => setEvaluatingSupplierId(expanded ? supplier.id : null)}
                  variant="outlined"
                  disableGutters
                  sx={{
                    border: '1px solid',
                    borderColor: isExpanded ? 'primary.main' : 'divider',
                    borderRadius: '8px !important',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'text.primary' }} />}
                    sx={{
                      bgcolor: (theme) =>
                        isYellow
                          ? alpha('#ffd54f', 0.4)
                          : alpha(theme.palette[statusColor].main, 0.15),
                      color: 'text.primary',
                      '&:hover': {
                        bgcolor: (theme) =>
                          isYellow
                            ? alpha('#ffd54f', 0.6)
                            : alpha(theme.palette[statusColor].main, 0.25),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        pr: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: isExpanded ? 'bold' : 'medium' }}
                        >
                          {supplier.name}
                        </Typography>
                        {hasIngestionWarning && (
                          <Tooltip title="One or more documents failed text extraction. The AI is analyzing the raw PDF as a fallback, which may impact accuracy. Please manually verify this submission.">
                            <WarningAmberIcon color="warning" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                      <Chip
                        label={statusLabel}
                        color={isYellow ? 'default' : statusColor}
                        size="small"
                        sx={{
                          bgcolor: 'background.paper',
                          color: isYellow ? '#d84315' : `${statusColor}.main`,
                          fontWeight: 'bold',
                        }}
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ bgcolor: 'background.default', p: { xs: 1, md: 3 } }}>
                    <SupplierAssessmentMatrix
                      supplier={supplier}
                      rubric={rubric}
                      procurement={procurement}
                      initialScores={userEval?.scores || EMPTY_ARRAY}
                      initialOverallComments={userEval?.overallComments || ''}
                      isSubmitted={userEval?.status === 'SUBMITTED'}
                      onSaveEvaluation={(scores, comments, isAutoSave, status) =>
                        handleSaveEvaluation(supplier.id, scores, comments, isAutoSave, status)
                      }
                      onShowSnackbar={(msg, sev) => showSnackbar(msg, sev)}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </Box>
      )}

      {tab === 4 && (!rubric || suppliers.length === 0) && (
        <Alert severity="warning">Rubric or suppliers missing for assessment.</Alert>
      )}

      {tab === 5 && isPanelChair && rubric && (
        <ModerationConsole
          procurementId={procurement.id}
          evaluations={evaluations}
          rubric={rubric}
          suppliers={suppliers}
          evaluatorAssignments={evaluatorAssignments}
          quickAnalyses={quickAnalyses}
          onShowSnackbar={(msg, sev) => showSnackbar(msg, sev)}
        />
      )}

      {tab === 5 && isPanelChair && !rubric && (
        <Alert severity="warning">Rubric missing for moderation.</Alert>
      )}

      {tab === 6 && isPanelChair && rubric && (
        <FinalReportExport
          procurementId={procurement.id}
          procurement={procurement}
          onSaveSettings={async (updatedSettings) => {
            try {
              const { doc, updateDoc } = await import('firebase/firestore');
              const { firestore: db } = await import('../services/firebase');
              await updateDoc(doc(db, 'procurements', procurement.id), {
                settings: updatedSettings,
              });
              showSnackbar('AI Settings updated successfully.', 'success');
            } catch (err) {
              showSnackbar(`Failed to update settings: ${(err as Error).message}`, 'error');
            }
          }}
          evaluations={evaluations}
          rubric={rubric}
          suppliers={suppliers}
          assessments={assessments}
          evaluatorAssignments={evaluatorAssignments}
          onShowSnackbar={(msg, sev) => showSnackbar(msg, sev)}
        />
      )}

      {tab === 6 && isPanelChair && !rubric && (
        <Card>
          <CardHeader
            title="Export & Finalize"
            subheader="Compile your procurement evaluation into formal documents or presentations."
          />
          <Divider />
          <CardContent>
            <Alert severity="warning">Rubric missing for final report generation.</Alert>
          </CardContent>
        </Card>
      )}

      {assignEvaluatorSupplierId && (
        <AssignEvaluatorModal
          open={true}
          onClose={() => setAssignEvaluatorSupplierId(null)}
          procurementId={procurement.id}
          supplierId={assignEvaluatorSupplierId}
          supplierName={suppliers.find((s) => s.id === assignEvaluatorSupplierId)?.name || ''}
          evaluatorAssignments={evaluatorAssignments.filter(
            (a) => a.supplierId === assignEvaluatorSupplierId
          )}
          allSuppliers={suppliers}
          allAssignments={evaluatorAssignments}
          onShowSnackbar={(msg: string, sev: 'success' | 'error' | 'warning' | 'info') =>
            showSnackbar(msg, sev)
          }
        />
      )}
    </Box>
  );
};

export default ProcurementDetailPage;
